import React, { useEffect, useState } from 'react'

import {
  Button,
  Flex,
  LoadingPlaceholder,
  Modal,
  ModalInjectedProps,
  Tooltip,
} from '@sketch/components'
import { ChangeBillingPlanPicker } from './ChangeBillingPlanPicker'
import { MonthlyToYearlyContent } from './MonthlyToYearlyContent'
import { YearlyToMonthlyContent } from './YearlyToMonthlyContent'

import { useToast } from '@sketch/toasts'

import {
  useGetAvailablePlansQuery,
  useChangePlanMutation,
  PaymentDetailsFragment,
  BillingSeatsInfoFragment,
  CloudBillingPlanFragment,
} from '@sketch/gql-types'

// This is a valid use case to narrow down Component type
// eslint-disable-next-line no-restricted-imports
import { Currency } from '@sketch/gql-types/expansive'

import { QuestionMarkIcon, TooltipText } from './ChangeBillingPlanModal.styles'

const TOOLTIP_TEXT = (
  <>
    <TooltipText>
      <b>How Pricing Works</b>
    </TooltipText>
    <TooltipText>
      <b>Viewers</b> can browse, inspect and comment on documents in the web
      app. Adding Viewers is free!
    </TooltipText>
    <TooltipText>
      <b>Editors</b> can do all of the above, and create and edit documents in
      the Mac app. You can add more Editors to the Workspace later.
    </TooltipText>
  </>
)

interface ChangeBillingPlanModalProps extends ModalInjectedProps {
  currentPlan?: CloudBillingPlanFragment
  customerId: string
  customerCurrency: Currency
  seats: BillingSeatsInfoFragment
  paymentDetails?: PaymentDetailsFragment
  onWorkspaceSettingRefresh: () => Promise<void>
}

export const ChangeBillingPlanModal: React.FC<ChangeBillingPlanModalProps> = ({
  hideModal,
  currentPlan,
  customerId,
  customerCurrency,
  seats,
  paymentDetails,
  onWorkspaceSettingRefresh,
}) => {
  const { showToast } = useToast()
  const [selectedPlan, setSelectedPlan] = useState(currentPlan)
  const [appliedDiscount, setAppliedDiscount] = useState<string | null>(null)

  // Queries and Mutations
  const { data, loading: loadingAvailablePlans } = useGetAvailablePlansQuery({})
  const [changePlan, { loading: loadingMutation }] = useChangePlanMutation({
    onError: 'show-toast',
    onCompleted: () => {
      onWorkspaceSettingRefresh()
      showToast(
        selectedPlan?.type === 'YEARLY'
          ? 'Your plan was updated'
          : 'Your plan will be updated in the next billing date'
      )
      hideModal()
    },
    variables: {
      input: {
        customerId,
        promotionCode: appliedDiscount,
      },
    },
    awaitRefetchQueries: true,
  })

  const availablePlans = data?.availablePlans || []

  const cloudPlans = availablePlans.filter(
    plan => plan.product === 'TEAMS'
  ) as CloudBillingPlanFragment[]

  /**
   * Safeguard current plan in case it's no longer available in the API response
   * This can occur when there's a price increase and the change is not reflected yet (scheduled)
   */
  const safeguardedCurrentPlan = cloudPlans.find(plan => {
    if (currentPlan?.id === plan.id) {
      return currentPlan
    }

    if (
      currentPlan?.product === plan.product &&
      currentPlan?.type === plan.type
    ) {
      return plan
    }

    return null
  })

  /**
   * Update the initially selected plan to the safeguarded one
   * in case the current plan is no longer available in the API response
   */
  useEffect(() => {
    if (currentPlan && currentPlan !== safeguardedCurrentPlan) {
      setSelectedPlan(safeguardedCurrentPlan)
    }
  }, [currentPlan, safeguardedCurrentPlan])

  const plans = [...cloudPlans].sort(a => (a.type === 'MONTHLY' ? -1 : 0))

  return (
    <Modal onCancel={hideModal}>
      <Modal.Header>Change Plan</Modal.Header>
      <Modal.Body>
        <Flex alignItems="center" pb={24}>
          You only pay for users who edit Sketch documents.{' '}
          <Tooltip
            disableWhenTouchDevice={false}
            tooltipContainerAs="span"
            content={TOOLTIP_TEXT}
          >
            <QuestionMarkIcon />
          </Tooltip>
        </Flex>
        {loadingAvailablePlans && (
          <Flex
            height={224}
            w="100%"
            justifyContent="center"
            alignItems="center"
          >
            <LoadingPlaceholder size="64px" />
          </Flex>
        )}
        {data && (
          <>
            <ChangeBillingPlanPicker
              customerCurrency={customerCurrency}
              onChange={setSelectedPlan}
              plans={plans}
              selectedPlan={selectedPlan}
              currentPlan={safeguardedCurrentPlan}
            />
            {currentPlan?.type === 'YEARLY' &&
              selectedPlan?.type === 'MONTHLY' && <YearlyToMonthlyContent />}
          </>
        )}
      </Modal.Body>
      {currentPlan?.type === 'MONTHLY' && selectedPlan?.type === 'YEARLY' && (
        <MonthlyToYearlyContent
          selectedPlan={selectedPlan}
          customerId={customerId}
          customerCurrency={customerCurrency}
          seats={seats}
          paymentDetails={paymentDetails}
          discountCode={appliedDiscount}
          onValidDiscount={setAppliedDiscount}
          onClearDiscountCode={() => setAppliedDiscount(null)}
        />
      )}
      <Modal.Footer>
        <Button
          onClick={hideModal}
          variant="secondary"
          disabled={loadingMutation}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={
            loadingAvailablePlans ||
            currentPlan?.id === selectedPlan?.id ||
            selectedPlan?.type !== 'YEARLY'
          }
          onClick={() => changePlan()}
          loading={loadingMutation}
        >
          Change Plan
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
